import { Component, Input, ViewChild } from '@angular/core'
import { HttpParams } from '@angular/common/http'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { WjChartModule, WjFlexChart } from '@grapecity/wijmo.angular2.chart'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import { BaseComponent, Grid, ListItem, replaceListItemsIfChanged } from '../../../shared'
import { GridExportComponent } from '../../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../../shared/utility/auto-size.directive'

interface JobTypeYearAmount {
    jobType: string
    year: number
    actualRevenue: number
    projectedRevenue: number
    actualLabor: number
    projectedLabor: number
    actualMaterials: number
    projectedMaterials: number
    actualEquipment: number
    projectedEquipment: number
    actualSubcontract: number
    projectedSubcontract: number
    actualOther: number
    projectedOther: number
    actualOverhead: number
    projectedOverhead: number
}

interface JobTypeSummaryRow {
    jobType: string
    revenue: number
    totalCost: number
    gm: number
    gmPercent: number | null
    labor: number
    laborPercent: number | null
    materials: number
    materialsPercent: number | null
    equipment: number
    equipmentPercent: number | null
    subcontract: number
    subcontractPercent: number | null
    other: number
    otherPercent: number | null
    overhead: number
    overheadPercent: number | null
}

@Component({
    selector: 'app-job-type-summary',
    templateUrl: './job-type-summary.component.html',
    standalone: true,
    imports: [
        FormsModule,
        FaIconComponent,
        WjInputModule,
        WjChartModule,
        WjGridModule,
        WjGridFilterModule,
        AutoSizeDirective,
        GridExportComponent,
        GridSelectionCalculationComponent,
    ],
})
export class JobTypeSummaryComponent extends BaseComponent {
    private static readonly JOB_STATUS_STORAGE_KEY = 'projections-job-type-summary-status'
    private static readonly YEARS_STORAGE_KEY = 'projections-job-type-summary-years'
    private static readonly TAB_ID = 11

    @ViewChild('summaryChart') summaryChart?: WjFlexChart

    @Input()
    set active(value: number) {
        if (value !== JobTypeSummaryComponent.TAB_ID) return
        if (!this.started) {
            this.started = true
            this.start()
        }
        setTimeout(() => this.summaryChart?.invalidate(), 60)
    }

    faSpinner = faSpinner
    grid: Grid<JobTypeSummaryRow>
    raw: JobTypeYearAmount[] = []

    statusOptions: ListItem<number>[] = [
        new ListItem(1, 'Active'),
        new ListItem(2, 'Soft Closed'),
        new ListItem(3, 'Hard Closed'),
        new ListItem(0, 'Pending'),
    ]
    availableYears: ListItem<number>[] = []
    jobTypeOptions: ListItem<string>[] = []

    openMonth = new Date()
    projectionStartMonth: Date | null = null
    includeProjected = false

    private started = false
    private openMonthReady = false
    private yearListReady = false
    private jobTypeListReady = false
    private lastRequestKey = ''
    private persistedYearSelection: number[] | null = null

    get projectionStartMonthWarning(): string {
        if (!this.projectionStartMonth) return ''
        if (this.projectionStartMonth > this.openMonth)
            return 'Projection Start Month is beyond the open WIP month. Actuals are limited to closed months only.'
        return ''
    }

    constructor() {
        super()
        this.restoreJobStatusSelection()
        this.persistedYearSelection = this.readStoredNumbers(JobTypeSummaryComponent.YEARS_STORAGE_KEY)
        this.grid = new Grid<JobTypeSummaryRow>(this.services, 'jobType', this)
            .wrapColumnHeaders()
            .isReadOnly(true)
            .editOnDoubleClick(false)
    }

    private start() {
        this.cancelableRequest(
            'JobTypeSummaryOpenMonth',
            true,
            this.services.data.get<string>('/api/Projections/GetOpenMonth').subscribe({
                next: result => {
                    this.openMonth = new Date(result)
                    this.projectionStartMonth = new Date(this.openMonth)
                    setTimeout(() => {
                        this.openMonthReady = true
                        this.loadSummary()
                    })
                },
            }),
        )
    }

    jobStatusChanged() {
        this.writeStoredNumbers(
            JobTypeSummaryComponent.JOB_STATUS_STORAGE_KEY,
            this.statusOptions.filter(item => item.$checked).map(x => x.value),
        )
        this.loadSummary()
    }

    projectionStartMonthChanged() {
        this.loadSummary()
    }

    yearsChanged() {
        if (!this.yearListReady) return
        const selectedYears = this.availableYears.filter(x => x['checked']).map(x => x.value)
        this.persistedYearSelection = selectedYears
        this.writeStoredNumbers(JobTypeSummaryComponent.YEARS_STORAGE_KEY, selectedYears)
        this.applyFilters()
    }

    applyFilters() {
        const selectedYears = new Set(this.availableYears.filter(x => x['checked']).map(x => x.value))
        const selectedTypes = new Set(this.jobTypeOptions.filter(x => x.$checked).map(x => x.value))
        if (selectedYears.size === 0 || (this.jobTypeOptions.length > 0 && selectedTypes.size === 0)) {
            this.publish([])
            return
        }

        const totals = new Map<string, JobTypeSummaryRow>()
        for (const source of this.raw) {
            if (!selectedYears.has(source.year)) continue
            if (!selectedTypes.has(source.jobType)) continue

            let row = totals.get(source.jobType)
            if (!row) {
                row = this.emptyRow(source.jobType)
                totals.set(source.jobType, row)
            }
            row.revenue += this.amount(source.actualRevenue, source.projectedRevenue)
            row.labor += this.amount(source.actualLabor, source.projectedLabor)
            row.materials += this.amount(source.actualMaterials, source.projectedMaterials)
            row.equipment += this.amount(source.actualEquipment, source.projectedEquipment)
            row.subcontract += this.amount(source.actualSubcontract, source.projectedSubcontract)
            row.other += this.amount(source.actualOther, source.projectedOther)
            row.overhead += this.amount(source.actualOverhead, source.projectedOverhead)
        }

        const rows = Array.from(totals.values())
            .map(row => this.finishRow(row))
            .sort((a, b) => a.jobType.localeCompare(b.jobType))
        this.publish(rows)
    }

    private loadSummary() {
        if (!this.openMonthReady) return
        const statusString = this.statusOptions
            .filter(item => item.$checked)
            .map(x => x.value)
            .join(',')
        const requestKey = `${statusString}|${this.monthKey(this.projectionStartMonth)}`
        if (requestKey === this.lastRequestKey) return
        this.lastRequestKey = requestKey

        if (!statusString) {
            this.raw = []
            this.rebuildYearOptions()
            this.rebuildJobTypeOptions()
            this.applyFilters()
            this.initialLoadingComplete()
            return
        }

        let params = new HttpParams().set('jobStatusString', statusString)
        if (this.projectionStartMonth) {
            params = params.set('projectionStartMonth', this.projectionStartMonth.toISOString())
        }
        this.cancelableRequest(
            'JobTypeSummary',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/Projections/GetJobTypeSummary', params)).subscribe({
                next: result => {
                    this.raw = (result[0] ?? []) as JobTypeYearAmount[]
                    this.rebuildYearOptions()
                    this.rebuildJobTypeOptions()
                    this.applyFilters()
                    this.initialLoadingComplete()
                },
            }),
        )
    }

    private rebuildYearOptions() {
        const years = Array.from(new Set(this.raw.map(x => x.year))).sort((a, b) => b - a)
        const previous = new Map(this.availableYears.map(item => [item.value, !!item['checked']]))
        const currentYear = new Date().getFullYear()
        const next: ListItem<number>[] = []
        for (const year of years) {
            const item = new ListItem(year, year.toString())
            if (this.yearListReady) item['checked'] = previous.get(year) ?? false
            else if (this.persistedYearSelection?.length) item['checked'] = this.persistedYearSelection.includes(year)
            else item['checked'] = year === currentYear
            next.push(item)
        }
        if (!next.some(item => item['checked']) && next.length > 0) {
            const fallback = next.find(item => item.value === currentYear) ?? next[0]
            fallback['checked'] = true
        }
        this.availableYears = replaceListItemsIfChanged(this.availableYears, next)
        this.yearListReady = true
    }

    private rebuildJobTypeOptions() {
        const types = Array.from(new Set(this.raw.map(x => x.jobType))).sort((a, b) => a.localeCompare(b))
        const previous = new Map(this.jobTypeOptions.map(item => [item.value, item.$checked]))
        const next = types.map(type => {
            const item = new ListItem(type, type)
            item.$checked = this.jobTypeListReady ? (previous.get(type) ?? true) : true
            return item
        })
        this.jobTypeOptions = replaceListItemsIfChanged(this.jobTypeOptions, next)
        this.jobTypeListReady = true
    }

    private publish(rows: JobTypeSummaryRow[]) {
        this.grid.items = rows
        setTimeout(() => this.summaryChart?.invalidate(), 0)
    }

    private emptyRow(jobType: string): JobTypeSummaryRow {
        return {
            jobType,
            revenue: 0,
            totalCost: 0,
            gm: 0,
            gmPercent: null,
            labor: 0,
            laborPercent: null,
            materials: 0,
            materialsPercent: null,
            equipment: 0,
            equipmentPercent: null,
            subcontract: 0,
            subcontractPercent: null,
            other: 0,
            otherPercent: null,
            overhead: 0,
            overheadPercent: null,
        }
    }

    private finishRow(row: JobTypeSummaryRow): JobTypeSummaryRow {
        row.totalCost = row.labor + row.materials + row.equipment + row.subcontract + row.other + row.overhead
        row.gm = row.revenue - row.totalCost
        row.gmPercent = this.ratio(row.gm, row.revenue)
        row.laborPercent = this.ratio(row.labor, row.revenue)
        row.materialsPercent = this.ratio(row.materials, row.revenue)
        row.equipmentPercent = this.ratio(row.equipment, row.revenue)
        row.subcontractPercent = this.ratio(row.subcontract, row.revenue)
        row.otherPercent = this.ratio(row.other, row.revenue)
        row.overheadPercent = this.ratio(row.overhead, row.revenue)
        return row
    }

    private amount(actual: number, projected: number): number {
        return (actual || 0) + (this.includeProjected ? projected || 0 : 0)
    }

    private ratio(part: number, revenue: number): number | null {
        if (!revenue) return null
        return part / revenue
    }

    private monthKey(date: Date | null): string {
        if (!date) return ''
        return `${date.getFullYear()}-${date.getMonth()}`
    }

    private restoreJobStatusSelection() {
        const stored = this.readStoredNumbers(JobTypeSummaryComponent.JOB_STATUS_STORAGE_KEY)
        const matching = stored?.filter(value => this.statusOptions.some(item => item.value === value)) ?? []
        const statusesToCheck = matching.length > 0 ? matching : this.statusOptions.map(item => item.value)
        for (const item of this.statusOptions) {
            item.$checked = statusesToCheck.includes(item.value)
        }
    }

    private readStoredNumbers(key: string): number[] | null {
        try {
            const stored = localStorage.getItem(key)
            if (!stored) return null
            const parsed = JSON.parse(stored)
            if (!Array.isArray(parsed) || !parsed.every(value => typeof value === 'number')) return null
            return parsed
        } catch {
            return null
        }
    }

    private writeStoredNumbers(key: string, values: number[]) {
        try {
            localStorage.setItem(key, JSON.stringify(values))
        } catch {
            /* ignore storage failures */
        }
    }
}
